/**
 * Classification of Supabase auth errors surfaced by the login form, kept as
 * pure functions so the mapping can be unit-tested without rendering the form.
 *
 * Structured error codes (err.code) are preferred over message text, which is
 * provider-controlled and changes without notice.
 * @see https://supabase.com/docs/reference/javascript/auth-error-codes
 */

export const GENERIC_ERROR_MESSAGE = "An error occurred";
export const NO_ACCOUNT_MESSAGE = "No account found for that email.";
export const INVALID_CODE_MESSAGE =
  "Invalid or expired code. Request a new one below.";

/**
 * Send-path "no account" signals. The login form sends the OTP with
 * `shouldCreateUser: false`, so an email with no matching account comes back as
 * `otp_disabled` ("Signups not allowed for otp") — Supabase reuses that code to
 * mean "OTP sign-up is not permitted here", which for this form is exactly the
 * no-account case. It must classify as no-account here even though the same code
 * means "invalid/expired code" on the verify path (see INVALID_CODE_ERROR_CODES).
 */
const NO_ACCOUNT_ERROR_CODES = new Set([
  "signup_disabled",
  "user_not_found",
  "otp_disabled",
]);
const INVALID_CODE_ERROR_CODES = new Set([
  "otp_expired",
  "otp_disabled",
  "validation_failed",
]);

/**
 * Defensive secondary match on message text, preserving prior behaviour for
 * providers/SDK versions that omit a structured error code.
 */
const NO_ACCOUNT_MESSAGE_FRAGMENTS = ["signups not allowed", "user not found"];
const INVALID_CODE_MESSAGE_FRAGMENTS = ["expired", "invalid"];

/** Safely read a string `code` field off an unknown thrown value. */
function getErrorCode(err: unknown): string | undefined {
  if (typeof err === "object" && err !== null && "code" in err) {
    const { code } = err as { code: unknown };
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

function messageMatches(message: string, fragments: string[]): boolean {
  const lower = message.toLowerCase();
  return fragments.some((fragment) => lower.includes(fragment));
}

/** Map an OTP-send error to a user-facing message. */
export function formatSendError(err: unknown): string {
  if (err instanceof Error) {
    const code = getErrorCode(err);
    if (code && NO_ACCOUNT_ERROR_CODES.has(code)) {
      return NO_ACCOUNT_MESSAGE;
    }
    // Defensive secondary match on message text — runs even when a code is
    // present but unrecognised, so a provider message like "Signups not
    // allowed for otp" still maps to the no-account case.
    if (messageMatches(err.message, NO_ACCOUNT_MESSAGE_FRAGMENTS)) {
      return NO_ACCOUNT_MESSAGE;
    }
    return err.message;
  }
  return GENERIC_ERROR_MESSAGE;
}

/** Map an OTP-verify error to a user-facing message. */
export function formatVerifyError(err: unknown): string {
  if (err instanceof Error) {
    const code = getErrorCode(err);
    if (code) {
      return INVALID_CODE_ERROR_CODES.has(code)
        ? INVALID_CODE_MESSAGE
        : err.message;
    }
    if (messageMatches(err.message, INVALID_CODE_MESSAGE_FRAGMENTS)) {
      return INVALID_CODE_MESSAGE;
    }
    return err.message;
  }
  return GENERIC_ERROR_MESSAGE;
}
